
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Image, EyeIcon, Heart, Calendar, Edit, Trash2, Globe, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { UploadButton } from '@/components/ui/upload-button';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import { Story } from '@/types/content';
import { useStoriesManagement } from '@/hooks/useStoriesManagement';

const DoctorStoriesPage = () => {
  const { stories, loading, error, addStory, deleteStory, toggleVisibility, extendExpiry } = useStoriesManagement();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newStory, setNewStory] = useState<Partial<Story>>({
    title: '',
    description: '',
    image: ''
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  
  const handleAddStory = () => {
    if (!newStory.title || !uploadedFile) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // In a real app, we would upload the file here
    const imageUrl = URL.createObjectURL(uploadedFile);
    
    addStory({
      ...newStory,
      image: imageUrl,
      id: Date.now(),
      views: 0,
      likes: 0,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours from now
    } as Story);
    
    setIsAddModalOpen(false);
    setNewStory({ title: '', description: '', image: '' });
    setUploadedFile(null);
    
    toast.success("Story added successfully");
  };
  
  const handleExtendExpiry = (storyId: number) => {
    extendExpiry(storyId);
    toast.success("Story extended for 24 more hours");
  };
  
  const handleToggleVisibility = (storyId: number) => {
    toggleVisibility(storyId);
    toast.success("Story visibility updated");
  };
  
  const handleDeleteStory = (storyId: number) => {
    deleteStory(storyId);
    toast.success("Story deleted successfully");
  };
  
  const getTotalStats = () => {
    const activeStories = stories.filter(s => new Date(s.expiresAt!) > new Date());
    return {
      active: activeStories.length,
      totalViews: stories.reduce((sum, story) => sum + (story.views || 0), 0),
      totalLikes: stories.reduce((sum, story) => sum + (story.likes || 0), 0),
    };
  };
  
  const stats = getTotalStats();
  
  return (
    <div className="min-h-screen bg-background pb-20">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pb-24 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Image className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Manage My Stories</h1>
          </div>
          
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add New Story
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <div className="p-6 flex flex-col items-center text-center">
              <div className="bg-primary/10 p-3 rounded-full mb-3">
                <Image className="h-6 w-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{stats.active}</p>
              <p className="text-sm text-muted-foreground">Active Stories</p>
            </div>
          </Card>
          
          <Card>
            <div className="p-6 flex flex-col items-center text-center">
              <div className="bg-primary/10 p-3 rounded-full mb-3">
                <EyeIcon className="h-6 w-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{stats.totalViews}</p>
              <p className="text-sm text-muted-foreground">Total Views</p>
            </div>
          </Card>
          
          <Card>
            <div className="p-6 flex flex-col items-center text-center">
              <div className="bg-primary/10 p-3 rounded-full mb-3">
                <Heart className="h-6 w-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{stats.totalLikes}</p>
              <p className="text-sm text-muted-foreground">Total Likes</p>
            </div>
          </Card>
        </div>
        
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="active">Active Stories</TabsTrigger>
            <TabsTrigger value="expired">Expired Stories</TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories
                .filter(story => new Date(story.expiresAt!) > new Date())
                .map(story => (
                  <Card key={story.id} className="overflow-hidden">
                    <div className="aspect-video relative">
                      <img 
                        src={story.image} 
                        alt={story.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {story.views && story.views > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <EyeIcon className="h-3 w-3" /> {story.views}
                          </Badge>
                        )}
                        {story.likes && story.likes > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Heart className="h-3 w-3" /> {story.likes}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1">{story.title}</h3>
                      {story.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {story.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>
                            Expires: {format(new Date(story.expiresAt!), 'MMM d, h:mm a')}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                    <div className="px-4 pb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleToggleVisibility(story.id)}
                          title={story.isPublic ? "Make Private" : "Make Public"}
                        >
                          {story.isPublic ? 
                            <Globe className="h-4 w-4 text-green-500" /> : 
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          }
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleExtendExpiry(story.id)}
                          title="Extend by 24 hours"
                        >
                          <Calendar className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteStory(story.id)}
                          title="Delete Story"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={story.isPublic} 
                          onCheckedChange={() => handleToggleVisibility(story.id)}
                        />
                        <span className="text-xs text-muted-foreground">
                          {story.isPublic ? "Public" : "Private"}
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
                
              {stories.filter(story => new Date(story.expiresAt!) > new Date()).length === 0 && (
                <Card className="col-span-3 p-6 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Image className="h-8 w-8 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-medium">No active stories</h3>
                    <p className="text-sm text-muted-foreground">
                      Your active stories will appear here. Add a new story to get started.
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-2"
                      onClick={() => setIsAddModalOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Story
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="expired">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories
                .filter(story => new Date(story.expiresAt!) <= new Date())
                .map(story => (
                  <Card key={story.id} className="overflow-hidden opacity-75">
                    <div className="aspect-video relative">
                      <img 
                        src={story.image} 
                        alt={story.title} 
                        className="w-full h-full object-cover filter grayscale"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {story.views && story.views > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <EyeIcon className="h-3 w-3" /> {story.views}
                          </Badge>
                        )}
                        {story.likes && story.likes > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Heart className="h-3 w-3" /> {story.likes}
                          </Badge>
                        )}
                      </div>
                      <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                        <Badge variant="outline" className="text-lg py-1 px-3">Expired</Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1">{story.title}</h3>
                      {story.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {story.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>
                            Expired: {format(new Date(story.expiresAt!), 'MMM d, h:mm a')}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                    <div className="px-4 pb-4 flex justify-between">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleExtendExpiry(story.id)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Republish
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteStory(story.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </Card>
                ))}
                
              {stories.filter(story => new Date(story.expiresAt!) <= new Date()).length === 0 && (
                <Card className="col-span-3 p-6 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Image className="h-8 w-8 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-medium">No expired stories</h3>
                    <p className="text-sm text-muted-foreground">
                      Your expired stories will appear here.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Story</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input 
                id="title" 
                placeholder="Story title" 
                value={newStory.title}
                onChange={(e) => setNewStory({...newStory, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Add a description"
                value={newStory.description}
                onChange={(e) => setNewStory({...newStory, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Image or Video</Label>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                {uploadedFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="relative w-full aspect-video bg-muted rounded-md overflow-hidden">
                      <img 
                        src={URL.createObjectURL(uploadedFile)}
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">{uploadedFile.name}</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setUploadedFile(null)}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <Image className="h-8 w-8 text-muted-foreground opacity-50" />
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Drag & drop an image/video or click to browse
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Recommended: 16:9 ratio, max 20MB
                      </p>
                    </div>
                    <Input
                      type="file"
                      accept="image/*,video/*"
                      className="hidden"
                      id="story-file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          setUploadedFile(e.target.files[0]);
                        }
                      }}
                    />
                    <label htmlFor="story-file">
                      <Button variant="outline" size="sm" asChild>
                        <span>Browse Files</span>
                      </Button>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                id="visibility"
                checked={newStory.isPublic}
                onCheckedChange={(checked) => setNewStory({...newStory, isPublic: checked})}
              />
              <Label htmlFor="visibility">Make story public</Label>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAddStory}>
              Publish Story
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorStoriesPage;
